import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import { useState } from "react";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  classeController,
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import { updateStudentSchema } from "../../validators/eleve.schema.ts";

// student skeleton component
const StudentsPageSkeleton = ({
  isDarkMode,
  rows = 6,
}: {
  isDarkMode: boolean;
  rows?: number;
}) => {
  const bgCard = isDarkMode ? "bg-[#161B22]" : "bg-gray-50/80";
  const bgPage = isDarkMode ? "bg-[#0D1117]" : "bg-white";
  const border = isDarkMode ? "border-[#30363D]" : "border-gray-200";
  const skeleton = "bg-gray-300 dark:bg-gray-700";

  return (
    <div className="space-y-6 animate-pulse">
      <div className={`rounded-2xl overflow-hidden border shadow-sm ${border}`}>
        <div className={`p-5 ${bgPage}`}>
          {/* ===== STATS ===== */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`p-4 rounded-xl border ${bgCard} ${border}`}
              >
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className={`h-3 w-28 rounded ${skeleton}`} />
                    <div className={`h-7 w-20 rounded ${skeleton}`} />
                  </div>
                  <div className={`h-12 w-12 rounded-full ${skeleton}`} />
                </div>
              </div>
            ))}
          </div>

          {/* ===== SEARCH & FILTER ===== */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className={`h-10 flex-grow rounded ${skeleton}`} />
            <div className={`h-10 w-40 rounded ${skeleton}`} />
            <div className={`h-10 w-48 rounded ${skeleton}`} />
          </div>

          {/* ===== TABLE ===== */}
          <div
            className={`rounded-2xl overflow-hidden border shadow-sm ${border}`}
          >
            {/* Table header */}
            <div
              className={`grid grid-cols-5 gap-4 px-6 py-3 ${
                isDarkMode ? "bg-[#161B22]" : "bg-white"
              }`}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`h-3 rounded ${skeleton}`} />
              ))}
            </div>

            {/* Rows */}
            <div className={`${bgPage}`}>
              {[...Array(rows)].map((_, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-5 gap-4 px-5 py-3 border-t ${border}`}
                >
                  {/* Name */}
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full ${skeleton}`} />
                    <div className="space-y-2">
                      <div className={`h-3 w-32 rounded ${skeleton}`} />
                      <div className={`h-2 w-20 rounded ${skeleton}`} />
                    </div>
                  </div>

                  <div className={`h-3 w-24 rounded ${skeleton}`} />
                  <div className={`h-5 w-20 rounded-full ${skeleton}`} />
                  <div className={`h-5 w-16 rounded-full ${skeleton}`} />

                  {/* Actions */}
                  <div className="flex justify-center gap-3">
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== PAGINATION ===== */}
          <div className="mt-4">
            <div className={`h-3 w-48 rounded ${skeleton}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Ligne d'information en lecture seule pour le popup de détail
const DetailField = ({
  label,
  value,
  isDarkMode,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  isDarkMode: boolean;
  icon?: string;
}) => (
  <div>
    <p
      className={`text-xs font-medium mb-0.5 ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-sm flex items-center gap-1.5 ${
        isDarkMode ? "text-gray-100" : "text-gray-900"
      }`}
    >
      {icon && <i className={`fas ${icon} text-xs opacity-60`}></i>}
      {value || "-"}
    </p>
  </div>
);

const Student: React.FC = () => {
  const { isDarkMode } = useOutletContext<OutletContextType>();
  const [notification, setNotification] = useState<any>(null); // { message, type }
  const [showEditDialog, setShowEditDialog] = useState<any>(false); // { message, type }
  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Detail popup (informations de l'élève)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);

  const handleRowClick = (item: any) => {
    setSelectedStudent(item);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedStudent(null);
  };

  //eleve data state

  const [currentId, setCurrentId] = useState<any>(null); // { message, type }
  const [formData, setFormData] = useState<{
    nom: string;
    postnom: string;
    prenom: string;
    genre: string;
    ville: string;
    commune: string;
    quartier: string;
    avenue: string;
    telephone: string;
    uid: string;
    date_naissance: string;
    lieu_naissance: string;
    parent_id: any;
  }>({
    // Informations personnelles

    nom: "",
    postnom: "",
    prenom: "",
    genre: "",
    ville: "",
    commune: "",
    quartier: "",
    avenue: "",
    telephone: "",
    uid: "",
    date_naissance: "",
    lieu_naissance: "",
    parent_id: "",
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Pré-remplit et ouvre le formulaire d'édition. Réutilisée par le bouton
  // "éditer" de la ligne ET par le popup de détail (même logique, aucun
  // nouveau setter introduit).
  const openEditForStudent = (item: any) => {
    setCurrentId(item.eleve_id);
    setFormData({
      nom: item.nom,
      postnom: item.postnom,
      prenom: item.prenom,
      genre: item.sexe,
      ville: item.ville,
      commune: item.commune,
      quartier: item.quartier,
      avenue: item.avenue,
      telephone: item.telephone,
      uid: item.uid,
      date_naissance: item.date_naissance,
      lieu_naissance: item.lieu_naissance,
      parent_id: item.parent_id,
    });
    setShowEditDialog(true);
  };

  //filter for Student
  const [filtrerStudentBySy, setFiltrerStudentBySy] = useState("");
  const [filtrerStudentStatus, setfiltrerStudentStatus] = useState<any>("Tous");
  const [filtrerAffectationStatus, setfiltrerAffectationStatus] =
    useState<any>("Tous");
  const [searchQuery, setSearchQuery] = useState("");

  //dropdown filter
  const [isSchoolYearDropdownOpen, setIsSchoolYearDropdownOpen] =
    useState(false);
  const [isEleveStatusDropdownOpen, setIsEleveStatusDropdownOpen] =
    useState(false);
  const [isAffectationStatusDropdownOpen, setIsAffectationStatusDropdownOpen] =
    useState(false);

  //toggle dropDown function
  const toggleSchoolYearDropdown = () => {
    setIsSchoolYearDropdownOpen(!isSchoolYearDropdownOpen);
  };
  const toggleEleveStatusDropdown = () => {
    setIsEleveStatusDropdownOpen(!isEleveStatusDropdownOpen);
  };
  const toggleAffectationStatusDropdown = () => {
    setIsAffectationStatusDropdownOpen(!isAffectationStatusDropdownOpen);
  };

  //UseReducer for Desactivate, Activate
  //handleling delete & activate & desactivate items
  interface ActionState {
    actionSection: string;
    actionType: string;
    actionText: string;
    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";
    payload: { actionText: string; actionSection: string; currentId: any };
  }
  const ACTION_TYPES = {
    DELETE: "DELETE",
    DESACTIVATE: "DESACTIVATE",
    ACTIVATE: "ACTIVATE",
    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",
    actionType: "",
    actionText: "",
    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,
          actionType: "delete",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,
          actionType: "activate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,
          actionType: "desactivate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",
          actionType: "",
          actionText: "",
          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = React.useReducer(
    actionReducer,
    INITIAL_STATE,
  );

  //fetch eleves
  const { data: eleveData, isLoading: isLoadingEleves } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });
  //fetch promotion
  const { data: promotionsData, isLoading: isLoadingPromotions } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });

  //fetch anne scolaire
  const { data: schoolYearData, isLoading: isLoadingSchoolYears } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  //fetch classe
  const { data: classeData, isLoading: isLoadingClasses } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  const { data: parentData, isLoading: isLoadingParents } = useQuery({
    queryKey: ["parents"],
    queryFn: parentController.getAll,
  });

  const parentsDataArr = parentData?.data || [];
  const eleveDataArr = eleveData?.data || [];
  const promotionsDataArr = promotionsData?.data || [];
  const schoolYearDataArr = schoolYearData?.data || [];
  const classeDataArr = classeData?.data || [];

  const updatePromotionDataAr = promotionsDataArr.map((promo) => {
    let classeDesignation = "";
    let anneeDesignation = "";

    for (let classe of classeDataArr) {
      if (promo.classe_id === classe.id) {
        classeDesignation = classe.designation;
      }
    }

    for (let sY of schoolYearDataArr) {
      if (promo.annee_scolaire_id === sY.id) {
        anneeDesignation = sY.designation;
      }
    }
    return {
      ...promo,
      classeDesignation,
      anneeDesignation,
    };
  });

  //Eleve list affecter
  const updateEleveDataArr = eleveDataArr.map((eleve) => {
    let affected: boolean = false;
    let affectedClass = "";
    let anneeScolaire = "";
    let anneeScolaireId: any;
    let affectedClassId: any;
    // idEleve, selectedAnn, promAnn&promoElev,PromoCla
    for (let promo of updatePromotionDataAr) {
      if (
        filtrerStudentBySy === promo.anneeDesignation &&
        String(eleve.eleve_id) === String(promo.eleve_id)
      ) {
        affected = true;
        affectedClass = promo.classeDesignation;
        affectedClassId = promo.classe_id;
        anneeScolaire = promo.anneeDesignation;
        anneeScolaireId = promo.annee_scolaire_id;
      }
    }

    return {
      ...eleve,
      affected,
      classeDesignation: affectedClass || "Non affecté",
      anneeDesignation: anneeScolaire || filtrerStudentBySy,
      anneeScolaireId,
      classeId: affectedClassId,
    };
  });

  const filteredEleves = updateEleveDataArr.filter((eleve) => {
    const matchesQuery =
      eleve.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      eleve.postnom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      eleve.prenom.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStudentStatus =
      filtrerStudentStatus === "Tous" || filtrerStudentStatus == eleve.status;

    const matchesAffectationStatus =
      filtrerAffectationStatus === "Tous" ||
      filtrerAffectationStatus == eleve.affected;

    return matchesQuery && matchesStudentStatus && matchesAffectationStatus;
  });

  const assignedEleves = filteredEleves.filter((eleve) => {
    const matchesAffectationStatus = eleve.affected;
    return matchesAffectationStatus;
  });
  const unassignedEleves = filteredEleves.filter((eleve) => {
    const matchesAffectationStatus = !eleve.affected;
    return matchesAffectationStatus;
  });

  const archivedEleves = filteredEleves.filter((eleve) => {
    const matchesStudentStatus = !eleve.status;
    return matchesStudentStatus;
  });

  //statistic number
  let studentStats = {
    total: filteredEleves.length,
    assigned: assignedEleves.length,
    unassigned: unassignedEleves.length,
    archived: archivedEleves.length,
  };

  //get active school year
  const activeYear = schoolYearDataArr.filter((item) => item.status)[0]
    ?.designation;

  const activeYearId = schoolYearDataArr.filter((item) => item.status)[0]?.id;

  // Initialiser le filtre sur l'année active dès le chargement des données.
  React.useEffect(() => {
    if (activeYear && !filtrerStudentBySy) {
      setFiltrerStudentBySy(activeYear);
    }
  }, [activeYear, filtrerStudentBySy]);

  const isSelectedYearActive = filtrerStudentBySy === activeYear;

  const [showAffectionModal, setShowAffectionModal] = useState(false);
  const [newPromotion, setNewPromotion] = useState<{
    classe_id: any;
    annee_scolaire_id: any;
    eleve_id: any;
  }>({
    classe_id: "",
    annee_scolaire_id: "",
    eleve_id: "",
  });

  // ===== Affectation multiple =====
  // Tout se passe dans un popup dédié, indépendant des filtres du tableau
  // principal : année scolaire source, année scolaire cible (toujours
  // l'année active), classe source, classe cible. La liste des élèves n'est
  // affichée/activée que lorsque les 4 valeurs sont renseignées.
  const [showBulkAffectionModal, setShowBulkAffectionModal] = useState(false);
  const [bulkSourceYear, setBulkSourceYear] = useState(""); // désignation année source
  const [bulkSourceClasseId, setBulkSourceClasseId] = useState("");
  const [bulkTargetClasseId, setBulkTargetClasseId] = useState("");
  const [selectedBulkStudentIds, setSelectedBulkStudentIds] = useState<
    Set<any>
  >(new Set());
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  // Les 4 filtres doivent tous être renseignés (année cible = année active,
  // automatiquement définie dès qu'elle existe) pour "activer" la liste.
  const bulkListActivated = Boolean(
    bulkSourceYear && bulkSourceClasseId && bulkTargetClasseId && activeYearId,
  );

  // Liste calculée à partir des deux premiers filtres (année source + classe
  // source) : élèves actuellement affectés à cette classe, pour cette année.
  const bulkSourceStudents = React.useMemo(() => {
    if (!bulkSourceYear || !bulkSourceClasseId) return [];
    return eleveDataArr
      .map((eleve: any) => {
        let matchedPromo: any = null;
        for (const promo of updatePromotionDataAr) {
          if (
            promo.anneeDesignation === bulkSourceYear &&
            promo.eleve_id === eleve.eleve_id
          ) {
            matchedPromo = promo;
          }
        }
        return { ...eleve, matchedPromo };
      })
      .filter(
        (eleve: any) =>
          eleve.matchedPromo &&
          String(eleve.matchedPromo.classe_id) === String(bulkSourceClasseId),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSourceYear, bulkSourceClasseId, eleveDataArr, promotionsDataArr]);

  const isStudentAlreadyInTargetClass = (eleveId: any) => {
    if (!bulkTargetClasseId || !activeYearId) return false;

    return updatePromotionDataAr.some(
      (promo: any) =>
        String(promo.eleve_id) === String(eleveId) &&
        String(promo.classe_id) === String(bulkTargetClasseId) &&
        String(promo.annee_scolaire_id) === String(activeYearId),
    );
  };

  const selectableBulkIds = bulkSourceStudents
    .filter((e: any) => e.status && !isStudentAlreadyInTargetClass(e.eleve_id)) // les élèves désactivés (status 0) restent non sélectionnables
    .map((e: any) => e.eleve_id);

  const areAllBulkSelected =
    selectableBulkIds.length > 0 &&
    selectableBulkIds.every((id) => selectedBulkStudentIds.has(id));

  const toggleSelectBulkStudent = (eleveId: any) => {
    setSelectedBulkStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(eleveId)) {
        next.delete(eleveId);
      } else {
        next.add(eleveId);
      }
      return next;
    });
  };

  const toggleSelectAllBulk = () => {
    setSelectedBulkStudentIds((prev) => {
      if (areAllBulkSelected) {
        const next = new Set(prev);
        selectableBulkIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      selectableBulkIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const resetBulkAffectionState = () => {
    setBulkSourceYear("");
    setBulkSourceClasseId("");
    setBulkTargetClasseId("");
    setSelectedBulkStudentIds(new Set());
  };

  const closeBulkAffectionModal = () => {
    setShowBulkAffectionModal(false);
    resetBulkAffectionState();
  };

  const selectedBulkStudentsDetails = bulkSourceStudents.filter((e: any) =>
    selectedBulkStudentIds.has(e.eleve_id),
  );

  const bulkSourceClasseLabel = (() => {
    const classe = classeDataArr.find(
      (c) => String(c.id) === String(bulkSourceClasseId),
    );
    return classe ? capitalizeFirstLetter(classe.designation) : "";
  })();

  const bulkTargetClasseLabel = (() => {
    const classe = classeDataArr.find(
      (c) => String(c.id) === String(bulkTargetClasseId),
    );
    return classe ? capitalizeFirstLetter(classe.designation) : "";
  })();

  // console.log("==============");
  // console.log(newPromotion);
  // console.log(activeYear);
  // console.log(activeYearId);

  // Fermeture des menus lors d'un clic à l'extérieur
  const schoolYearDropdownRef = React.useRef<HTMLDivElement>(null);
  const studentStatusDropdownRef = React.useRef<HTMLDivElement>(null);
  const affectationStatusDropdownRef = React.useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const mutationCreatePromotion = useMutation({
    mutationFn: () =>
      promotionController.create({
        eleve_id: newPromotion.eleve_id,
        classe_id: newPromotion.classe_id,
        annee_scolaire_id: newPromotion.annee_scolaire_id,
      }),

    onSuccess: (data) => {
      // console.log("==================");
      // console.log(data);
      setShowAffectionModal(false);
      showNotification("Enregistré avec succès", "success");
      setNewPromotion({
        classe_id: "",
        annee_scolaire_id: "",
        eleve_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["promotions"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  // Affectation multiple : crée une promotion (classe cible, année active) pour
  // chaque élève sélectionné dans le popup. Suit le même schéma que
  // l'affectation individuelle (promotionController.create, pas de méthode
  // update disponible).
  const mutationBulkAffectStudents = useMutation({
    mutationFn: () =>
      Promise.all(
        Array.from(selectedBulkStudentIds).map((eleveId) =>
          promotionController.create({
            eleve_id: eleveId,
            classe_id: bulkTargetClasseId,
            annee_scolaire_id: activeYearId,
          }),
        ),
      ),

    onSuccess: () => {
      showNotification(
        `${selectedBulkStudentIds.size} élève(s) affecté(s) avec succès`,
        "success",
      );
      setShowBulkConfirmModal(false);
      closeBulkAffectionModal();
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["promotions"] });
    },

    onError: () => {
      showNotification(
        "Une erreur est survenue lors de l'affectation multiple, réessayer....",
        "error",
      );
    },
  });

  const handleOpenBulkConfirm = () => {
    if (selectedBulkStudentIds.size === 0)
      return showNotification("Aucun élève sélectionné", "erreur");
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAffect = () => {
    mutationBulkAffectStudents.mutate();
  };

  const mutationDesactivateStudent = useMutation({
    mutationFn: () => studentControler.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Désactivation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivateStudent = useMutation({
    mutationFn: () => studentControler.activate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "student"
    ) {
      mutationActivateStudent.mutate();
    }
  };
  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "student"
    ) {
      mutationDesactivateStudent.mutate();
    }
  };

  const handleAffect = () => {
    if (!isSelectedYearActive)
      return showNotification(
        "L'affectation est autorisée uniquement pour l'année active",
        "error",
      );
    if (
      !newPromotion.eleve_id ||
      !newPromotion.classe_id ||
      !newPromotion.annee_scolaire_id
    )
      return showNotification(
        "Veuillez sélectionner la promotion et l'année scolaire",
        "erreur",
      );
    mutationCreatePromotion.mutate();
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //status button

      if (
        schoolYearDropdownRef.current &&
        !schoolYearDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSchoolYearDropdownOpen(false);
      }
      if (
        studentStatusDropdownRef.current &&
        !studentStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEleveStatusDropdownOpen(false);
      }
      if (
        affectationStatusDropdownRef.current &&
        !affectationStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAffectationStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fonction pour exporter les données
  const exportData = () => {
    let data = filteredEleves.map((student) => ({
      Noms: ` ${student.nom} ${student.postnom} ${capitalizeFirstLetter(
        student.prenom,
      )}`,
      Classe: student.classeDesignation || "-",
      AnneeScolaire: student.anneeDesignation || "-",
      Status:
        student.status === 1 ? "Active" : student.status === 0 && "Desactive",
      Affectation: student.affected
        ? "Affecte"
        : !student.affected && "Non affecte",
    }));

    // Create Excel-like content
    if (data.length === 0)
      return showNotification("Aucun élève à exporter", "error");
    const headers = Object.keys(data[0]);
    const excelContent = [
      headers.join("\t"),
      ...data.map((row: any) =>
        headers.map((header) => row[header]).join("\t"),
      ),
    ].join("\n");
    // Create and trigger download
    const blob = new Blob([excelContent], {
      type: "text/tab-separated-values;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_${new Date().toISOString().split("T")[0]}.xls`;
    link.click();

    showNotification("Export Excel réussi");
  };

  const mutationUpdateStudent = useMutation({
    mutationFn: () =>
      studentControler.update(currentId, {
        nom: formData.nom,
        postnom: formData.postnom,
        prenom: formData.prenom,
        sexe: formData.genre,
        ville: formData.ville,
        commune: formData.commune,
        quartier: formData.quartier,
        avenue: formData.avenue,
        telephone: formData.telephone,
        uid: "12345678",
        date_naissance: formData.date_naissance,
        lieu_naissance: formData.lieu_naissance,
        parent_id: formData.parent_id,
      }),

    onSuccess: () => {
      setShowEditDialog(false);
      showNotification("Modification effectuée avec succès", "success");
      setCurrentId("");
      setFormData({
        nom: "",
        postnom: "",
        prenom: "",
        genre: "",
        ville: "",
        commune: "",
        quartier: "",
        avenue: "",
        telephone: "",
        uid: "",
        date_naissance: "",
        lieu_naissance: "",
        parent_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleUpdateStudent = () => {
    const zodValidationSchema = updateStudentSchema.safeParse(formData);
    if (!zodValidationSchema.success) {
      const errors = zodValidationSchema.error.flatten();
      const firstError = Object.values(errors.fieldErrors)[0][0];
      return showNotification(firstError, "error");
    }

    mutationUpdateStudent.mutate();
  };

  // Nom complet du parent d'un élève, pour l'affichage dans le popup de
  // détail (simple lecture de parentsDataArr, déjà chargé pour le select
  // du formulaire — aucune nouvelle requête).
  const getParentFullName = (parentId: any) => {
    const parent = parentsDataArr.find((p) => p.parent_id === parentId);
    if (!parent) return null;
    return `${capitalizeFirstLetter(parent.nom)} ${capitalizeFirstLetter(
      parent.postnom,
    )} ${capitalizeFirstLetter(parent.prenom)}`;
  };

  // Identité visuelle commune avec la page Scolarité.
  const studentCardClass = `overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-[#30363D] bg-[#0D1117] shadow-black/10" : "border-gray-200 bg-white shadow-gray-200/40"}`;
  const studentTableClass = `w-full min-w-0 overflow-auto rounded-xl border ${isDarkMode ? "border-[#30363D]" : "border-gray-200"}`;
  const studentInputClass = `h-9 rounded-lg border text-[11px] outline-none transition-all ${isDarkMode ? "border-[#30363D] bg-[#0D1117] text-gray-200 placeholder:text-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10" : "border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"}`;
  const studentFilterClass = `inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[10px] font-medium transition-colors ${isDarkMode ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`;
  const studentActionClass = (tone: "green" | "red" | "blue") =>
    `inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150 ${isDarkMode ? "border-[#30363D] " + (tone === "red" ? "text-red-400 hover:bg-red-500/10" : tone === "blue" ? "text-blue-400 hover:bg-blue-500/10" : "text-emerald-400 hover:bg-emerald-500/10") : "border-gray-200 " + (tone === "red" ? "text-red-600 hover:bg-red-50" : tone === "blue" ? "text-blue-600 hover:bg-blue-50" : "text-emerald-600 hover:bg-emerald-50")}`;
  const statusPill = (
    active: boolean,
    labelActive: string,
    labelInactive: string,
  ) => (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-semibold ${
        active
          ? isDarkMode
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-emerald-50 text-emerald-600"
          : isDarkMode
            ? "bg-red-500/10 text-red-400"
            : "bg-red-50 text-red-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-red-500"
        }`}
      ></span>
      {active ? labelActive : labelInactive}
    </span>
  );
  return (
    <>
      {isLoadingEleves ||
      isLoadingClasses ||
      isLoadingParents ||
      isLoadingSchoolYears ||
      isLoadingPromotions ? (
        <StudentsPageSkeleton isDarkMode={isDarkMode} />
      ) : (
        <div className="min-w-0 w-full space-y-4">
          {" "}
          <div className={studentCardClass}>
            <div
              className={`p-4 sm:p-5 ${isDarkMode ? "bg-[#0D1117] text-gray-200" : "bg-white text-gray-800"}`}
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Total Élèves
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.total}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                      }`}
                    >
                      <i
                        className={`fas fa-users ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Affectés
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.assigned}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-emerald-500/10" : "bg-emerald-50"
                      }`}
                    >
                      <i
                        className={`fas fa-check-circle ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Non Affectés
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.unassigned}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-amber-500/10" : "bg-amber-50"
                      }`}
                    >
                      <i
                        className={`fas fa-exclamation-circle ${
                          isDarkMode ? "text-yellow-400" : "text-yellow-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Désactivés
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.archived}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-gray-800" : "bg-gray-200"
                      }`}
                    >
                      <i
                        className={`fas fa-archive ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              {/* Filters and Actions */}
              <div className="flex flex-wrap items-center gap-2.5 mb-5">
                <div className="flex-grow relative min-w-[220px]">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    <i className="fas fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un élève..."
                    className={`${studentInputClass} w-full pl-10 pr-4`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={toggleSchoolYearDropdown}
                    className={`${studentFilterClass} whitespace-nowrap`}
                  >
                    <span>{filtrerStudentBySy}</span>
                    <i className="fas fa-chevron-down text-xs opacity-60"></i>
                  </button>
                  {isSchoolYearDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-1.5 w-40 rounded-xl shadow-xl overflow-hidden ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } border ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      } z-50`}
                      ref={schoolYearDropdownRef}
                    >
                      <div className="py-1">
                        {schoolYearDataArr.map((year) => (
                          <button
                            key={year.id}
                            onClick={() => {
                              setFiltrerStudentBySy(year.designation);
                              setIsSchoolYearDropdownOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#21262D] text-gray-200"
                                : "hover:bg-gray-100 text-gray-700"
                            } !rounded-button whitespace-nowrap`}
                          >
                            {year.designation}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={toggleEleveStatusDropdown}
                    className={`${studentFilterClass} whitespace-nowrap`}
                  >
                    <span>
                      Status:{" "}
                      {filtrerStudentStatus === 1
                        ? "Active"
                        : filtrerStudentStatus === 0
                          ? "Désactivé"
                          : "Tous"}
                    </span>
                    <i className="fas fa-chevron-down text-xs opacity-60"></i>
                  </button>
                  {isEleveStatusDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-1.5 w-40 rounded-xl shadow-xl overflow-hidden ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } border ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      } z-50`}
                      ref={studentStatusDropdownRef}
                    >
                      <div className="py-1">
                        {[
                          { id: 0, designation: "Tous", status: "Tous" },
                          { id: 1, designation: "Active", status: 1 },
                          { id: 2, designation: "Désactivé", status: 0 },
                        ].map((status) => (
                          <button
                            key={status.id}
                            onClick={() => {
                              setfiltrerStudentStatus(status.status);
                              setIsEleveStatusDropdownOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#21262D] text-gray-200"
                                : "hover:bg-gray-100 text-gray-700"
                            } !rounded-button whitespace-nowrap`}
                          >
                            {status.designation}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={toggleAffectationStatusDropdown}
                    className={`${studentFilterClass} whitespace-nowrap`}
                  >
                    <span>
                      Affectation:{" "}
                      {filtrerAffectationStatus === 1
                        ? "Affecté"
                        : filtrerAffectationStatus == 0
                          ? "Non affecté"
                          : "Tous"}
                    </span>
                    <i className="fas fa-chevron-down text-xs opacity-60"></i>
                  </button>
                  {isAffectationStatusDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-1.5 w-40 rounded-xl shadow-xl overflow-hidden ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } border ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      } z-50`}
                      ref={affectationStatusDropdownRef}
                    >
                      <div className="py-1">
                        {[
                          { id: 0, designation: "Tous", status: "Tous" },
                          { id: 1, designation: "Affecté", status: 1 },
                          { id: 2, designation: "Non ffecté", status: 0 },
                        ].map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              setfiltrerAffectationStatus(item.status);
                              setIsAffectationStatusDropdownOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#21262D] text-gray-200"
                                : "hover:bg-gray-100 text-gray-700"
                            } !rounded-button whitespace-nowrap`}
                          >
                            {item.designation}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Link
                  to={"/eleves/nouveau"}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm ${
                    isDarkMode
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-green-500 hover:bg-green-600"
                  } text-white !rounded-button whitespace-nowrap`}
                >
                  <i className="fas fa-plus"></i>Ajouter un élève
                </Link>

                <button
                  onClick={() => setShowBulkAffectionModal(true)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm ${
                    isDarkMode
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-yellow-500 hover:bg-yellow-600"
                  } text-white !rounded-button whitespace-nowrap`}
                >
                  <i className="fas fa-people-arrows"></i>Affectation multiple
                </button>

                <button
                  onClick={exportData}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm ${
                    isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-500 hover:bg-blue-600"
                  } text-white !rounded-button whitespace-nowrap`}
                >
                  <i className="fas fa-file-import"></i>Exporter
                </button>
              </div>
              {/* Students Table */}
              <div className={studentTableClass}>
                <table
                  className={`w-full min-w-[900px] text-xs divide-y ${isDarkMode ? "divide-[#30363D]" : "divide-gray-200"}`}
                >
                  {" "}
                  <thead
                    className={`${isDarkMode ? "bg-[#161B22]" : "bg-white"}`}
                  >
                    <tr>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Nom & Prénom
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Classe
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Année Scolaire
                      </th>

                      <th
                        scope="col"
                        className={`px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      ></th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${
                      isDarkMode ? "bg-[#0D1117]" : "bg-white"
                    } divide-y ${
                      isDarkMode ? "divide-[#21262D]" : "divide-gray-100"
                    }`}
                  >
                    {filteredEleves
                      .sort((a, b) => a.nom.localeCompare(b.nom))
                      .map((item, index) => {
                        return (
                          <tr
                            key={index}
                            onClick={() => handleRowClick(item)}
                            className={`group cursor-pointer transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#161B22]"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-9 w-9">
                                  <div
                                    className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                                    }`}
                                  >
                                    <i className="fas fa-user"></i>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <span
                                    className={`px-0 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      isDarkMode
                                        ? "text-gray-200"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {capitalizeFirstLetter(item.nom)}{" "}
                                    {capitalizeFirstLetter(item.postnom)}{" "}
                                    {capitalizeFirstLetter(item.prenom)}
                                  </span>{" "}
                                  <div
                                    className={`text-sm ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    <span className="mr-2 text-ellipsis text-[11px]">
                                      {" "}
                                      ID: STD-00{index + 1}
                                    </span>
                                    {statusPill(
                                      !!item.status,
                                      "Actif",
                                      "Désactivé",
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap font-medium text-center ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {item.affected ? (
                                item.classeDesignation
                              ) : (
                                <span
                                  className={
                                    isDarkMode
                                      ? "text-amber-400"
                                      : "text-amber-600"
                                  }
                                >
                                  Non affecté
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              {filtrerStudentBySy}
                            </td>

                            <td className="w-[170px] min-w-[170px] px-4 py-3 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditForStudent(item);
                                  }}
                                  className={studentActionClass("green")}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    item.status
                                      ? dispatch({
                                          type: "DESACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir désactiver cet élève?",
                                            currentId: item.eleve_id,
                                            actionSection: "student",
                                          },
                                        })
                                      : dispatch({
                                          type: "ACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir Activer cet élève?",
                                            currentId: item.eleve_id,
                                            actionSection: "student",
                                          },
                                        });
                                  }}
                                  className={`${
                                    item.status
                                      ? "text-green-500 hover:text-green-700"
                                      : "text-gray-500 hover:text-green-700"
                                  } h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-green-500/10 cursor-pointer whitespace-nowrap`}
                                >
                                  <i className="fa-solid fa-circle-check"></i>
                                </button>
                                {!item.affected && (
                                  <button
                                    type="button"
                                    disabled={
                                      // !item.status || !isSelectedYearActive
                                      !item.status
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setNewPromotion({
                                        classe_id: "",
                                        annee_scolaire_id: activeYearId,
                                        eleve_id: item.eleve_id,
                                      });
                                      setShowAffectionModal(true);
                                    }}
                                    className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2 text-[10px] font-semibold transition-colors ${
                                      !item.status || !isSelectedYearActive
                                        ? "cursor-not-allowed opacity-40 border-gray-500/30 text-gray-500"
                                        : isDarkMode
                                          ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                          : "border-amber-200 text-amber-700 hover:bg-amber-50"
                                    }`}
                                    title={
                                      !item.status
                                        ? "Élève désactivé"
                                        : !isSelectedYearActive
                                          ? `Affectation uniquement pour l'année en cours (${activeYear})`
                                          : "Affecter cet élève"
                                    }
                                  >
                                    <i className="fas fa-user-plus"></i>{" "}
                                    Affecter
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div
                className={`mt-3 flex items-center justify-end rounded-lg border px-3 py-2 ${isDarkMode ? "border-[#30363D] bg-[#161B22]/60" : "border-gray-200 bg-gray-50/60"}`}
              >
                <div
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Affichage de {studentStats.total} élèves
                </div>
              </div>
            </div>
          </div>
          {showAffectionModal && (
            <div
              id="addTerminalModal"
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <div
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D]"
                    : "bg-white border-gray-200"
                } rounded-2xl border shadow-2xl w-full max-w-lg mx-4 overflow-hidden`}
              >
                <div
                  className={`px-5 py-4 border-b ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDarkMode
                          ? "bg-yellow-500/10 text-yellow-400"
                          : "bg-yellow-50 text-yellow-600"
                      }`}
                    >
                      <i className="fas fa-archive"></i>
                    </div>
                    <h3 className="text-base font-semibold leading-tight">
                      Affectation
                    </h3>
                  </div>
                  <button
                    id="closeTerminalModalBtn"
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                    onClick={() => {
                      setShowAffectionModal(false);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <label
                      className={`block text-[10px] font-semibold mb-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Nouvelle Promotion <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newPromotion.classe_id}
                      onChange={(e) => {
                        setNewPromotion((prev) => ({
                          ...prev,
                          classe_id: e.target.value,
                        }));
                      }}
                      className={`w-full h-10 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                          : "bg-gray-50 border-gray-200 text-gray-900"
                      }`}
                      required
                    >
                      <option value="">Sélectionner une classe</option>
                      {classeDataArr.map((item) => (
                        <option key={item.id} value={item.id}>
                          {capitalizeFirstLetter(item.designation)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-[10px] font-semibold mb-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Année scolaire <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newPromotion.annee_scolaire_id}
                      onChange={(e) => {
                        setNewPromotion((prev) => ({
                          ...prev,
                          annee_scolaire_id: e.target.value,
                        }));
                      }}
                      className={`w-full h-10 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                          : "bg-gray-50 border-gray-200 text-gray-900"
                      }`}
                      required
                    >
                      <option value="">Selectionner année scolaire</option>
                      <option value={activeYearId}>{activeYear}</option>
                    </select>
                  </div>
                </div>
                <div
                  className={`px-5 py-3 border-t ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-end gap-3`}
                >
                  <button
                    disabled={mutationCreatePromotion.isPending}
                    onClick={() => {
                      setShowAffectionModal(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } ${mutationCreatePromotion.isPending ? "cursor-not-allowed opacity-60" : ""}`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={mutationCreatePromotion.isPending}
                    onClick={() => {
                      handleAffect();
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    } ${mutationCreatePromotion.isPending ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    {mutationCreatePromotion.isPending
                      ? "Affectation en cours..."
                      : "Affecter"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ===== Popup Affectation multiple ===== */}
          {showBulkAffectionModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDarkMode
                          ? "bg-yellow-500/10 text-yellow-400"
                          : "bg-yellow-50 text-yellow-600"
                      }`}
                    >
                      <i className="fas fa-people-arrows"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold leading-tight">
                        Affectation multiple
                      </h3>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        Déplacez tous les élèves d'une classe source vers une
                        nouvelle classe
                      </p>
                    </div>
                  </div>
                  <button
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                    onClick={closeBulkAffectionModal}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                  {/* Les 4 filtres */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        className={`block text-xs font-medium mb-1.5 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Année scolaire source{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={bulkSourceYear}
                        onChange={(e) => {
                          setBulkSourceYear(e.target.value);
                          setSelectedBulkStudentIds(new Set());
                        }}
                        className={`w-full h-10 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                            : "bg-gray-50 border-gray-200 text-gray-900"
                        }`}
                      >
                        <option value="">Sélectionner année source</option>
                        {schoolYearDataArr.map((year) => {
                          //if (year.designation === activeYear) jump to next iteration
                          if (year.designation === activeYear) return null;
                          return (
                            <option key={year.id} value={year.designation}>
                              {year.designation}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-xs font-medium mb-1.5 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Année scolaire cible
                      </label>
                      <select
                        disabled
                        className={`w-full h-10 px-3 rounded-lg border text-xs cursor-not-allowed ${
                          isDarkMode
                            ? "bg-[#0D1117] border-[#30363D] text-gray-400"
                            : "bg-gray-100 border-gray-200 text-gray-500"
                        }`}
                      >
                        <option>{activeYear || "Aucune année active"}</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-xs font-medium mb-1.5 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Classe source <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={bulkSourceClasseId}
                        onChange={(e) => {
                          setBulkSourceClasseId(e.target.value);
                          setSelectedBulkStudentIds(new Set());
                        }}
                        disabled={!bulkSourceYear}
                        className={`w-full h-10 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                          !bulkSourceYear
                            ? isDarkMode
                              ? "bg-[#0D1117]/60 border-gray-800 text-gray-600 cursor-not-allowed"
                              : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                            : isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                        }`}
                      >
                        <option value="">Sélectionner classe source</option>
                        {classeDataArr.map((item) => (
                          <option key={item.id} value={item.id}>
                            {capitalizeFirstLetter(item.designation)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-xs font-medium mb-1.5 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Classe cible <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={bulkTargetClasseId}
                        onChange={(e) => setBulkTargetClasseId(e.target.value)}
                        className={`w-full h-10 px-3 rounded-lg border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                            : "bg-gray-50 border-gray-200 text-gray-900"
                        }`}
                      >
                        <option value="">Sélectionner classe cible</option>
                        {classeDataArr.map((item) => (
                          <option key={item.id} value={item.id}>
                            {capitalizeFirstLetter(item.designation)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Liste des élèves : visible, mais inerte, tant que les 4
                      filtres ne sont pas tous renseignés. */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label
                        className={`block text-sm font-medium ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Élèves de la classe source
                        {bulkSourceStudents.length > 0 &&
                          ` (${bulkSourceStudents.length})`}
                      </label>
                      {bulkListActivated && selectableBulkIds.length > 0 && (
                        <label
                          className={`flex items-center gap-2 text-xs font-medium cursor-pointer ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={areAllBulkSelected}
                            onChange={toggleSelectAllBulk}
                            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          Tout sélectionner
                        </label>
                      )}
                    </div>

                    <div
                      className={`rounded-xl border overflow-hidden ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      }`}
                    >
                      {!bulkSourceYear || !bulkSourceClasseId ? (
                        <div
                          className={`px-4 py-10 text-center text-sm ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          <i className="fas fa-filter text-xl mb-2 block opacity-40"></i>
                          Sélectionnez l'année et la classe source pour afficher
                          les élèves.
                        </div>
                      ) : bulkSourceStudents.length === 0 ? (
                        <div
                          className={`px-4 py-10 text-center text-sm ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          <i className="fas fa-user-slash text-xl mb-2 block opacity-40"></i>
                          Aucun élève affecté à cette classe pour cette année
                          scolaire.
                        </div>
                      ) : (
                        <>
                          {!bulkListActivated && (
                            <div
                              className={`px-4 py-2.5 text-xs flex items-center gap-2 border-b ${
                                isDarkMode
                                  ? "bg-yellow-500/5 border-[#30363D] text-yellow-400"
                                  : "bg-yellow-50 border-gray-200 text-yellow-700"
                              }`}
                            >
                              <i className="fas fa-circle-info"></i>
                              Sélectionnez aussi la classe cible pour activer la
                              sélection des élèves.
                            </div>
                          )}
                          <div className="max-h-64 overflow-y-auto divide-y divide-gray-700/50">
                            {bulkSourceStudents
                              .sort((a, b) => a.nom.localeCompare(b.nom))
                              .map((item: any) => {
                                const isAlreadyAffected =
                                  isStudentAlreadyInTargetClass(item.eleve_id);

                                const isDisabledRow =
                                  !bulkListActivated ||
                                  !item.status ||
                                  isAlreadyAffected;
                                const isChecked = selectedBulkStudentIds.has(
                                  item.eleve_id,
                                );
                                return (
                                  <div
                                    key={item.eleve_id}
                                    className={`flex items-center gap-3 px-4 py-2.5 ${
                                      isDisabledRow
                                        ? "opacity-50"
                                        : isDarkMode
                                          ? "hover:bg-gray-800"
                                          : "hover:bg-gray-50"
                                    } ${
                                      isDarkMode ? "bg-[#0D1117]" : "bg-white"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={isDisabledRow}
                                      checked={isChecked}
                                      onChange={() =>
                                        toggleSelectBulkStudent(item.eleve_id)
                                      }
                                      className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <span
                                      className={`text-sm flex-1 ${
                                        isDarkMode
                                          ? "text-gray-200"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {capitalizeFirstLetter(item.nom)}{" "}
                                      {capitalizeFirstLetter(item.postnom)}{" "}
                                      {capitalizeFirstLetter(item.prenom)}
                                    </span>
                                    {!item.status && (
                                      <span
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                          isDarkMode
                                            ? "bg-red-900/40 text-red-400"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        Désactivé
                                      </span>
                                    )}

                                    {isAlreadyAffected && item.status && (
                                      <span
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                          isDarkMode
                                            ? "bg-green-900/40 text-green-400"
                                            : "bg-green-100 text-green-800"
                                        }`}
                                      >
                                        Affecté
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex items-center justify-between gap-3`}
                >
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {selectedBulkStudentIds.size > 0
                      ? `${selectedBulkStudentIds.size} élève(s) sélectionné(s)`
                      : ""}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={closeBulkAffectionModal}
                      className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                        isDarkMode
                          ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                          : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      Annuler
                    </button>
                    <button
                      disabled={selectedBulkStudentIds.size === 0}
                      onClick={handleOpenBulkConfirm}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                        isDarkMode
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-blue-500 hover:bg-blue-600"
                      } ${
                        selectedBulkStudentIds.size === 0
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <i className="fas fa-check"></i>
                      Valider la sélection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* ===== Popup de confirmation de l'affectation multiple ===== */}
          {showBulkConfirmModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
              <div
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D]"
                    : "bg-white border-gray-200"
                } rounded-2xl border shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]`}
              >
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <h3 className="text-sm font-thin leading-tight flex items-center gap-2">
                    <i className="fas fa-triangle-exclamation text-yellow-500"></i>
                    Confirmer l'affectation multiple
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  <p
                    className={`text-sm font-light ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Vous êtes sur le point d'affecter{" "}
                    <span className="font-semibold">
                      {selectedBulkStudentIds.size} élève(s)
                    </span>{" "}
                    de{" "}
                    <span className="font-semibold">
                      {bulkSourceClasseLabel.toUpperCase()}
                    </span>{" "}
                    ({bulkSourceYear}) vers{" "}
                    <span className="font-semibold">
                      {bulkTargetClasseLabel.toUpperCase()}
                    </span>{" "}
                    pour l'année{" "}
                    <span className="font-semibold">{activeYear}</span>.
                  </p>
                  <div
                    className={`rounded-lg border max-h-56 overflow-y-auto divide-y divide-gray-700/50 ${
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }`}
                  >
                    {selectedBulkStudentsDetails.map((item: any) => (
                      <div
                        key={item.eleve_id}
                        className={`px-4 py-2 text-sm flex items-center gap-2 ${
                          isDarkMode
                            ? "bg-[#0D1117] text-gray-200"
                            : "bg-white text-gray-800"
                        }`}
                      >
                        <i
                          className={`fas fa-user text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        ></i>
                        {capitalizeFirstLetter(item.nom)}{" "}
                        {capitalizeFirstLetter(item.postnom)}{" "}
                        {capitalizeFirstLetter(item.prenom)}
                      </div>
                    ))}
                  </div>
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Cette action est irréversible depuis cette page.
                  </p>
                </div>

                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-end gap-3`}
                >
                  <button
                    disabled={mutationBulkAffectStudents.isPending}
                    onClick={() => setShowBulkConfirmModal(false)}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } ${
                      mutationBulkAffectStudents.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Retour
                  </button>
                  <button
                    disabled={mutationBulkAffectStudents.isPending}
                    onClick={handleConfirmBulkAffect}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    } ${
                      mutationBulkAffectStudents.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationBulkAffectStudents.isPending ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Affectation en cours...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        Confirmer l'affectation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Activate dialog */}
          {state_actions.actionType === "activate" && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6`}
              >
                <div className="text-xl font-medium mb-4">
                  Confirmer l'activation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    disabled={mutationActivateStudent.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationActivateStudent.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationActivateStudent.isPending}
                    onClick={handleActivate}
                    className={`px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationActivateStudent.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationActivateStudent.isPending
                      ? "Activation en cours..."
                      : "Confirmer l'activation"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Desactivate dialog */}
          {state_actions.actionType === "desactivate" && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6`}
              >
                <div className="text-xl font-medium mb-4">
                  Confirmer la désactivation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    disabled={mutationDesactivateStudent.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationDesactivateStudent.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationDesactivateStudent.isPending}
                    onClick={handleDesactivate}
                    className={`px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationDesactivateStudent.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationDesactivateStudent.isPending
                      ? "Désactivation en cours..."
                      : "Confirmer la désactivation"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ===== Détail élève (au clic sur une ligne) ===== */}
          {showDetailModal && selectedStudent && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={closeDetailModal}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-between items-start`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i className="fas fa-user-graduate text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold leading-tight">
                        {capitalizeFirstLetter(selectedStudent.nom)}{" "}
                        {capitalizeFirstLetter(selectedStudent.postnom)}{" "}
                        {capitalizeFirstLetter(selectedStudent.prenom)}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {selectedStudent.classeDesignation && (
                          <span
                            className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {selectedStudent.classeDesignation}
                            {selectedStudent.anneeDesignation
                              ? ` · ${selectedStudent.anneeDesignation}`
                              : ""}
                          </span>
                        )}
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            selectedStudent.status
                              ? isDarkMode
                                ? "bg-green-900/40 text-green-400"
                                : "bg-green-100 text-green-800"
                              : isDarkMode
                                ? "bg-yellow-900/40 text-yellow-400"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {selectedStudent.status ? "Actif" : "Désactivé"}
                        </span>
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            selectedStudent.affected
                              ? isDarkMode
                                ? "bg-green-900/40 text-green-400"
                                : "bg-green-100 text-green-800"
                              : isDarkMode
                                ? "bg-yellow-900/40 text-yellow-400"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {selectedStudent.affected ? "Affecté" : "Non affecté"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
                  {/* Identité */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-id-card text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Identité
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
                      <DetailField
                        label="Genre"
                        value={
                          selectedStudent.sexe === "M"
                            ? "Masculin"
                            : selectedStudent.sexe === "F"
                              ? "Féminin"
                              : "-"
                        }
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Date de naissance"
                        value={selectedStudent.date_naissance}
                        icon="fa-cake-candles"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Lieu de naissance"
                        value={
                          selectedStudent.lieu_naissance
                            ? capitalizeFirstLetter(
                                selectedStudent.lieu_naissance,
                              )
                            : "-"
                        }
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Adresse */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">
                      <DetailField
                        label="Avenue"
                        value={selectedStudent.avenue}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Quartier"
                        value={selectedStudent.quartier}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Commune"
                        value={selectedStudent.commune}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Ville"
                        value={selectedStudent.ville}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Contact & Parent */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-address-book text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Contact & Parent
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                      <DetailField
                        label="Téléphone"
                        value={selectedStudent.telephone}
                        icon="fa-phone"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Parent"
                        value={getParentFullName(selectedStudent.parent_id)}
                        icon="fa-people-roof"
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-end gap-3`}
                >
                  <button
                    onClick={closeDetailModal}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => {
                      openEditForStudent(selectedStudent);
                      closeDetailModal();
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <i className="fas fa-edit"></i>
                      Modifier
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Edit student Modal */}
          {showEditDialog && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[50] p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#161B22]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-5 py-4 border-b flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i className="fas fa-user-graduate"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold leading-tight">
                        Modifier élève
                      </h3>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Les champs marqués d'un * sont obligatoires
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditDialog(false);
                    }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
                  {/* Section: Identité */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-id-card text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Identité
                        <span className="ml-2 text-xs font-normal text-red-500">
                          (Principales)
                        </span>
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label
                          htmlFor="nom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          required
                          value={formData.nom}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="postnom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Post-Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="postnom"
                          name="postnom"
                          required
                          value={formData.postnom}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="prenom"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          required
                          value={formData.prenom}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
                      <div>
                        <label
                          htmlFor="genre"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Genre <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="genre"
                          name="genre"
                          required
                          value={formData.genre}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner</option>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="lieuxNaissance"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Lieu de naissance{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="lieuxnaissance"
                          name="lieu_naissance"
                          required
                          value={formData.lieu_naissance}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="dateNaissance"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Date de naissance{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id="date_naissance"
                          name="date_naissance"
                          required
                          value={formData.date_naissance}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Section: Adresse & Contact */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale & Contact
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="avenue"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Avenue <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="avenue"
                          id="avenue"
                          required
                          value={formData.avenue}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="quartier"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Quartier <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="quartier"
                          id="quartier"
                          required
                          value={formData.quartier}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <label
                          htmlFor="commune"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Commune <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="commune"
                          id="commune"
                          required
                          value={formData.commune}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                      <div>
                        <label
                          htmlFor="ville"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Ville <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="ville"
                          id="ville"
                          required
                          value={formData.ville}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="telephone"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          name="telephone"
                          id="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          className={`block w-full border rounded-lg shadow-sm py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-100"
                    }
                  />

                  {/* Section: Parent */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <i
                        className={`fas fa-people-roof text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Parent
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="idParent"
                          className={`block text-sm font-medium mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Parent
                        </label>
                        <select
                          name="idParent"
                          id="idParent"
                          required
                          value={formData.parent_id}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              parent_id:
                                e.target.value === ""
                                  ? null
                                  : parseInt(e.target.value),
                            }))
                          }
                          className={`block w-full h-11 border rounded-lg shadow-sm px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100"
                              : "bg-gray-50 border-gray-200 text-gray-900"
                          }`}
                        >
                          <option value="">Sélectionner parent</option>
                          {parentsDataArr
                            .sort((a, b) =>
                              a.nom
                                .toLowerCase()
                                .localeCompare(b.nom.toLowerCase()),
                            )
                            .map((item) => (
                              <option
                                key={item.parent_id}
                                value={item.parent_id}
                              >
                                {item.nom.toUpperCase()}{" "}
                                {item.postnom.toUpperCase()}{" "}
                                {item.prenom.toUpperCase()}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-5 py-3 border-t flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-end gap-3`}
                >
                  <button
                    disabled={mutationUpdateStudent.isPending}
                    onClick={() => {
                      setShowEditDialog(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } ${mutationUpdateStudent.isPending ? "cursor-not-allowed opacity-60" : ""}`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={mutationUpdateStudent.isPending}
                    onClick={() => {
                      // Handle save logic here
                      handleUpdateStudent();
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-500 hover:bg-blue-600"
                    } ${mutationUpdateStudent.isPending ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    {mutationUpdateStudent.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Chargement...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-check"></i>
                        Mettre à jour
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ===== NOTIFICATION ===== */}
          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 px-6 py-3 rounded-lg text-white shadow-lg transition-all duration-500 z-[60] ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Student;
